import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCapabilities, useMember } from '@/lib/use-session';
import { CirclesChrome } from '@/components/circles/CirclesChrome';
import {
  circleFilterArgs,
  circlesDirectoryListArgs,
  circlesDirectorySearchSchema,
  hasActiveCircleFilters,
  type CirclesDirectorySearch,
} from '@/components/circles/directory-filters';
import { CirclePreferencesAsk } from '@/components/circles/preferences';
import { GroupsListBody } from '@/components/group/index';
import { JourneyDirectoryHint } from '@/components/entry-journeys';
import { groupSummaryToCircleSummary } from '@/components/circles/adapters';
import type { CircleSummary } from '@/components/circles/sample-data';
import { groupKeys } from '@/lib/queries/groups';

const PAGE_SIZE = 24;
// The "mine" rail is a complete list, not a browse page — fetch a window
// large enough that no member's own circles are silently truncated.
const MINE_PAGE_SIZE = 100;

export const Route = createFileRoute('/_public/circles/')({
  validateSearch: (raw: Record<string, unknown>): CirclesDirectorySearch =>
    circlesDirectorySearchSchema.parse(raw),
  component: CirclesListPage,
});

function CirclesListPage() {
  const search = Route.useSearch();
  const member = useMember();
  const signedIn = member !== null;
  const filtersActive = hasActiveCircleFilters(search);
  // recs.preferences_modal for this viewer (false when signed out): off, the
  // directory asks nothing and fetches no preferences.
  const preferencesEnabled = useCapabilities()?.circlePreferences === true;

  // The server drops the similarity term of the ranked sort for anonymous
  // viewers, so browsing asks for it either way.
  const listArgs = circlesDirectoryListArgs(search, PAGE_SIZE);

  // Mine drives the Joined/Leading pills on directory cards and keeps
  // memberships out of the All grid. The matching query lists what the "My
  // Circles" section shows: the same request narrowed by the directory's
  // search and filters on the server. With none set the two share one cache
  // entry, so a first filter keeps the list on screen while it loads.
  // Disabled when signed out: the endpoint requires auth and the data is
  // irrelevant to the anon directory.
  const myCirclesQuery = useQuery({
    ...groupKeys.circle.mine({ includeArchived: true, pageSize: MINE_PAGE_SIZE }),
    enabled: signedIn,
  });
  const myMatchingQuery = useQuery({
    ...groupKeys.circle.mine({
      includeArchived: true,
      pageSize: MINE_PAGE_SIZE,
      filters: circleFilterArgs(search),
    }),
    enabled: signedIn,
  });

  const toSummaries = (data: typeof myCirclesQuery.data): readonly CircleSummary[] | null =>
    data ? data.items.map((m) => groupSummaryToCircleSummary(m, { role: m.role })) : null;

  return (
    <CirclePreferencesAsk enabled={preferencesEnabled}>
      {(ask) => (
        <CirclesChrome signedIn={signedIn} filterActions={ask.button}>
          <JourneyDirectoryHint journey="join-circle" />
          <GroupsListBody
            kind="circle"
            listArgs={listArgs}
            mine={toSummaries(myCirclesQuery.data)}
            mineMatching={toSummaries(myMatchingQuery.data)}
            isError={
              (myCirclesQuery.isError && myCirclesQuery.data === undefined) ||
              (myMatchingQuery.isError && myMatchingQuery.data === undefined)
            }
            signedIn={signedIn}
            filtersActive={filtersActive}
            preferencesCard={ask.card}
            afterPreferencesCard={ask.afterCard}
          />
        </CirclesChrome>
      )}
    </CirclePreferencesAsk>
  );
}
